import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ContainerModal from './modal-container';
import { NewEmailType } from 'types/new-user';
import { useCreateUser } from 'views/contacts/hooks/useCreateUser';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import validations from 'utils/validations';

type DialogAccessProps = {
  open: boolean;
  onClose: () => void;
  partyId: string;
  onSuccess: (user_id: string, username: string, emails: NewEmailType[]) => void;
};

type createAccessForm = {
  email: string;
};

const CreateAccessModal: FC<DialogAccessProps> = ({ open, onClose, partyId, onSuccess }) => {
  const { t } = useTranslation();
  const schema = z.object({
    email: validations.email(t('validations:invalidEmail'), t('validations:required')),
  });
  const methods = useForm<createAccessForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const { control, handleSubmit, formState: { errors } } = methods;
  const { mutate: createUserMutation, ...responseCreateUserMutation } = useCreateUser({
    onSuccess(data) {
      responseCreateUserMutation.reset();
      const { username, _id, emails } = data.createUser;
      onSuccess(_id, username, emails);
      onClose();
    },
    onError() {
      responseCreateUserMutation.reset();
    },
  });

  return (
    <ContainerModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => {
        handleSubmit((data) => {
          createUserMutation({
            id: partyId,
            username: data.email,
          });
        })();
      }}
      loadingSaveButton={responseCreateUserMutation.isLoading}
      modalTitle={t('contact:addAccessModalTitle')}
      disableSavebutton={!methods.formState.isValid}
    >
      <Grid container direction="row">
        <Grid item xs={12}>
          <Controller
            control={control}
            name="email"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                disabled={responseCreateUserMutation.isLoading}
                label={t('contact:emailAddressLabel')}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </ContainerModal>
  );
};

export default CreateAccessModal;
